import { useEffect } from 'react'

const CHATBASE_SCRIPT_ID = 'chatbase-embed-script'
const CHATBASE_DOMAIN = 'www.chatbase.co'
const CHATBASE_HIDDEN_ATTRIBUTE = 'data-mbt-chatbase-hidden'
const CHATBASE_MOBILE_HIDE_STYLE_ID = 'chatbase-mobile-hide-style'
const CHATBASE_CLEANUP_DELAY_MS = 1000
const CHATBASE_INJECTED_NODE_SELECTORS = [
	'iframe[src*="chatbase.co"]',
	'iframe[src*="chatbase.ai"]',
	'[id*="chatbase"]',
]
const CHATBASE_LAUNCHER_IDS = [
	'chatbase-bubble-button',
	'chatbase-message-bubbles',
]

let chatbaseCleanupTimeoutId: number | null = null
let chatbaseLauncherObserver: MutationObserver | null = null

declare global {
	interface Window {
		embeddedChatbotConfig?: {
			chatbotId: string
			domain: string
		}
		chatbase?: {
			open?: () => void
			close?: () => void
			resetChat?: () => void
			// Some integrations also expose `addEventListener`, but we don't rely on it here.
		}
	}
}

type ChatbaseWidgetProps = {
	/**
	 * When false, we ensure the widget is not visible and the embed script is removed.
	 * This is important for auth routes (login/register) and post-logout.
	 */
	enabled: boolean
}

const clearScheduledChatbaseCleanup = () => {
	if (chatbaseCleanupTimeoutId !== null) {
		window.clearTimeout(chatbaseCleanupTimeoutId)
		chatbaseCleanupTimeoutId = null
	}
}

const stopObservingChatbaseLauncher = () => {
	chatbaseLauncherObserver?.disconnect()
	chatbaseLauncherObserver = null
}

const isChatbaseLauncherNode = (node: HTMLElement) => {
	if (CHATBASE_LAUNCHER_IDS.includes(node.id)) return true

	return (
		node.id.startsWith('chatbase-bubble-button') ||
		node.id.startsWith('chatbase-message-bubbles')
	)
}

const getInjectedChatbaseNodes = () =>
	document.querySelectorAll(CHATBASE_INJECTED_NODE_SELECTORS.join(','))

const hideNode = (node: HTMLElement) => {
	node.setAttribute(CHATBASE_HIDDEN_ATTRIBUTE, 'true')
	node.style.setProperty('display', 'none', 'important')
	node.style.setProperty('visibility', 'hidden', 'important')
	node.style.setProperty('pointer-events', 'none', 'important')
}

const restoreHiddenChatbaseNodes = () => {
	try {
		const nodes = getInjectedChatbaseNodes()
		nodes.forEach((node) => {
			if (!(node instanceof HTMLElement)) return
			if (node.getAttribute(CHATBASE_HIDDEN_ATTRIBUTE) !== 'true') return
			if (isMobile() && isChatbaseLauncherNode(node)) return

			node.style.removeProperty('display')
			node.style.removeProperty('visibility')
			node.style.removeProperty('pointer-events')
			node.removeAttribute(CHATBASE_HIDDEN_ATTRIBUTE)
		})
	} catch {
		// ignore
	}
}

const hideChatbaseDom = () => {
	try {
		const injectedNodes = getInjectedChatbaseNodes()
		injectedNodes.forEach((node) => {
			if (!(node instanceof HTMLElement)) return
			hideNode(node)
		})
	} catch {
		// ignore
	}
}

const destroyChatbase = () => {
	// Best-effort: close the widget if the API is available.
	try {
		window.chatbase?.close?.()
	} catch {
		// ignore
	}

	// Remove the embed script if present.
	document.getElementById(CHATBASE_SCRIPT_ID)?.remove()
	document.getElementById(CHATBASE_MOBILE_HIDE_STYLE_ID)?.remove()
	stopObservingChatbaseLauncher()

	// Remove DOM nodes injected by Chatbase once its internal listeners have had
	// a chance to settle after route/auth changes.
	const injectedNodes = getInjectedChatbaseNodes()
	injectedNodes.forEach((node) => node.remove())

	// Clear the global config so it doesn't leak across auth transitions.
	try {
		delete window.embeddedChatbotConfig
	} catch {
		// ignore
	}
}

const scheduleChatbaseCleanup = () => {
	clearScheduledChatbaseCleanup()

	// Hide immediately so UX stays the same, but delay destruction to avoid
	// third-party resize handlers trying to postMessage into a removed iframe.
	hideChatbaseDom()

	try {
		window.chatbase?.close?.()
	} catch {
		// ignore
	}

	chatbaseCleanupTimeoutId = window.setTimeout(() => {
		chatbaseCleanupTimeoutId = null
		destroyChatbase()
	}, CHATBASE_CLEANUP_DELAY_MS)
}

const isMobile = () =>
	typeof window !== 'undefined' &&
	window.matchMedia('(max-width: 1023px)').matches

const ensureMobileChatbaseLauncherHideStyle = () => {
	if (document.getElementById(CHATBASE_MOBILE_HIDE_STYLE_ID)) return

	const style = document.createElement('style')
	style.id = CHATBASE_MOBILE_HIDE_STYLE_ID
	style.textContent = `
		@media (max-width: 1023px) {
			#chatbase-bubble-button,
			#chatbase-message-bubbles,
			[id^="chatbase-bubble-button"],
			[id^="chatbase-message-bubbles"] {
				display: none !important;
				visibility: hidden !important;
				pointer-events: none !important;
				opacity: 0 !important;
			}
		}
	`

	document.head.appendChild(style)
}

const observeChatbaseLauncherOnMobile = () => {
	if (!isMobile() || !document.body) return

	stopObservingChatbaseLauncher()
	chatbaseLauncherObserver = new MutationObserver(() => {
		hideChatbaseLauncher()
	})
	chatbaseLauncherObserver.observe(document.body, {
		childList: true,
		subtree: true,
		attributes: true,
		attributeFilter: ['style', 'class', 'id'],
	})
}

const startMobileChatbaseLauncherProtection = () => {
	if (!isMobile()) return

	ensureMobileChatbaseLauncherHideStyle()
	observeChatbaseLauncherOnMobile()
	hideChatbaseLauncher()
}

const hideChatbaseLauncher = () => {
	// On mobile we don't want the floating launcher bubble at all.
	// Chat should only be opened via the "Support chat" entry.
	try {
		// These ids are currently used by Chatbase embed.
		const ids = ['chatbase-bubble-button', 'chatbase-message-bubbles']
		for (const id of ids) {
			const el = document.getElementById(id)
			if (el && el instanceof HTMLElement) {
				hideNode(el)
			}
		}

		// Fallback: hide any small chatbase element that looks like a launcher.
		const nodes = document.querySelectorAll(
			[
				'[id^="chatbase-"]',
				'iframe[src*="chatbase.co"]',
				'iframe[src*="chatbase.ai"]',
			].join(',')
		)
		nodes.forEach((node) => {
			if (!(node instanceof HTMLElement)) return
			const rect = node.getBoundingClientRect?.()
			const w = rect?.width ?? 0
			const h = rect?.height ?? 0
			const isSmall = w > 0 && h > 0 && w <= 140 && h <= 140
			if (isSmall) {
				hideNode(node)
			}
		})
	} catch {
		// ignore
	}
}

const CHATBASE_LAUNCHER_SELECTORS = [
	'#chatbase-bubble-button',
	'#chatbase-message-bubbles',
]

const CHATBASE_LAUNCHER_BASE_BOTTOM_PX = 20

export const setChatbaseLauncherBottomOffset = (bottomOffsetPx: number) => {
	try {
		const nodes = document.querySelectorAll(
			CHATBASE_LAUNCHER_SELECTORS.join(',')
		)
		nodes.forEach((node) => {
			if (!(node instanceof HTMLElement)) return
			node.style.setProperty(
				'bottom',
				`${Math.max(0, bottomOffsetPx)}px`,
				'important'
			)
		})
	} catch {
		// ignore
	}
}

export const resetChatbaseLauncherBottomOffset = () => {
	try {
		const nodes = document.querySelectorAll(
			CHATBASE_LAUNCHER_SELECTORS.join(',')
		)
		nodes.forEach((node) => {
			if (!(node instanceof HTMLElement)) return
			node.style.setProperty(
				'bottom',
				`${CHATBASE_LAUNCHER_BASE_BOTTOM_PX}px`,
				'important'
			)
		})
	} catch {
		// ignore
	}
}

export const openChatbase = () => {
	try {
		clearScheduledChatbaseCleanup()
		startMobileChatbaseLauncherProtection()
		restoreHiddenChatbaseNodes()
		window.chatbase?.open?.()
		// Some embeds need a second tick after being closed.
		setTimeout(() => window.chatbase?.open?.(), 50)
		// Keep launcher hidden on mobile even after opening.
		if (isMobile()) {
			setTimeout(() => hideChatbaseLauncher(), 0)
			setTimeout(() => hideChatbaseLauncher(), 250)
		}
	} catch {
		// ignore
	}
}

export const ChatbaseWidget = ({ enabled }: ChatbaseWidgetProps) => {
	useEffect(() => {
		if (!enabled) {
			scheduleChatbaseCleanup()
			return
		}

		const chatbotId = import.meta.env.VITE_PUBLIC_CHATBASE_CHATBOT_ID

		if (!chatbotId) {
			return
		}

		clearScheduledChatbaseCleanup()
		startMobileChatbaseLauncherProtection()
		restoreHiddenChatbaseNodes()

		const cleanup = () => {
			// On unmount (e.g. logout / route guard), hide immediately and destroy
			// after a short delay to avoid third-party teardown races.
			stopObservingChatbaseLauncher()
			scheduleChatbaseCleanup()
		}

		if (document.getElementById(CHATBASE_SCRIPT_ID)) {
			// Script already present (e.g. navigating within authenticated app).
			// Still ensure launcher is hidden on mobile.
			if (isMobile()) {
				hideChatbaseLauncher()
				const started = Date.now()
				const int = window.setInterval(() => {
					hideChatbaseLauncher()
					if (Date.now() - started > 3000) window.clearInterval(int)
				}, 250)
			}
			return cleanup
		}

		window.embeddedChatbotConfig = {
			chatbotId,
			domain: CHATBASE_DOMAIN,
		}

		const script = document.createElement('script')
		script.id = CHATBASE_SCRIPT_ID
		script.src = `https://${CHATBASE_DOMAIN}/embed.min.js`
		script.defer = true
		script.setAttribute('chatbotId', chatbotId)
		script.setAttribute('domain', CHATBASE_DOMAIN)

		// If user clicks "Support chat" before the API is ready, opening will be a no-op.
		// On load, try opening once to make repeated open/close flows reliable.
		script.onload = () => {
			// Hide the launcher immediately on mobile (and keep trying briefly in case
			// chatbase injects nodes asynchronously after script load).
			if (isMobile()) {
				hideChatbaseLauncher()
				setTimeout(() => hideChatbaseLauncher(), 250)
				const started = Date.now()
				const int = window.setInterval(() => {
					hideChatbaseLauncher()
					if (Date.now() - started > 3000) window.clearInterval(int)
				}, 250)
			}
		}

		document.body.appendChild(script)
		return cleanup
	}, [enabled])

	return null
}
